import { Button } from '@chakra-ui/react';
import type { ButtonProps } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useExportAction } from '@/hooks/actions/use-export-action';
import type { ExportResource } from './types';

export interface ExportButtonProps
  extends Omit<ButtonProps, 'onClick' | 'children' | 'loading' | 'isDisabled'> {
  /** Stable slug identifying the exportable resource. */
  resource: ExportResource;
  /** Filters forwarded to the backend — scoped by the caller. */
  filters: Record<string, unknown>;
  /** Optional label override. Defaults to `exports.action`. */
  label?: ReactNode;
  /** Optional icon rendered before the label. */
  leftIcon?: ReactNode;
  /** Allows the caller to disable the button (e.g. while data is loading). */
  isDisabled?: boolean;
}

/**
 * Reusable button that triggers a generic CSV export.
 *
 * The button is intentionally thin — all orchestration lives in
 * `useExportAction`, so any caller can drop `<ExportButton resource=... filters=... />`
 * and get the full sync/async flow for free.
 */
export const ExportButton = ({
  resource,
  filters,
  label,
  leftIcon,
  isDisabled,
  size = 'sm',
  variant = 'outline',
  colorPalette = 'gray',
  ...buttonProps
}: ExportButtonProps) => {
  const { t } = useTranslation();
  const { triggerExport, isExporting } = useExportAction();

  const handleClick = () => {
    triggerExport({ resource, filters });
  };

  return (
    <Button
      size={size}
      variant={variant}
      colorPalette={colorPalette}
      loading={isExporting}
      disabled={isDisabled || isExporting}
      onClick={handleClick}
      {...buttonProps}
    >
      {leftIcon ? <span style={{ marginRight: 6, display: 'inline-flex' }}>{leftIcon}</span> : null}
      {label ?? t('exports.action')}
    </Button>
  );
};

export default ExportButton;
